import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import OperatorLayout from './components/Layout/OperatorLayout';
import SupervisorLayout from './components/Layout/SupervisorLayout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AdminRoute from './components/Auth/AdminRoute';
import OperatorRoute from './components/Auth/OperatorRoute';
import SupervisorRoute from './components/Auth/SupervisorRoute';
import UserRedirect from './components/Auth/UserRedirect';

// Auth Pages
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';

// User Pages
import DashboardPage from './pages/Dashboard/DashboardPage';
import TemplatesPage from './pages/Templates/TemplatesPage';
import ProfilePage from './pages/Profile/ProfilePage';
import DisplayPageSimple from './pages/Display/DisplayPageSimple';

// Admin Pages
import AdminDashboardPage from './pages/Admin/AdminDashboardPage';
import AdminUsersPage from './pages/Admin/AdminUsersPage';
import AdminTemplatesPage from './pages/Admin/AdminTemplatesPage';
import AdminTemplateRequestsPage from './pages/Admin/AdminTemplateRequestsPage';
import AdminAuditLogPage from './pages/Admin/AdminAuditLogPage';
import AdminReportsPage from './pages/Admin/AdminReportsPage';
import AdminRulesPage from './pages/Admin/AdminRulesPage';
import AdminGatesPage from './pages/Admin/AdminGatesPage';
import AdminFlightSyncPage from './pages/Admin/AdminFlightSyncPage';
import AdminGateSessionsPage from './pages/Admin/AdminGateSessionsPage';
import AdminGateQueuePage from './pages/Admin/AdminGateQueuePage';

// Operator Pages
import GateSessionPage from './pages/Operator/GateSessionPage';
import SessionQueueRedirect from './pages/Operator/SessionQueueRedirect';
import AdHocPage from './pages/Operator/AdHocPage';
import RequestTemplatePage from './pages/Operator/RequestTemplatePage';
import RequestNewTemplatePage from './pages/Manager/RequestNewTemplatePage';
import StaffHelpPage from './pages/Operator/StaffHelpPage';
import OperatorApprovalsPage from './pages/Operator/OperatorApprovalsPage';

// Supervisor Pages
import SupervisorDashboardPage from './pages/Supervisor/SupervisorDashboardPage';
import SupervisorAnnouncementsPage from './pages/Supervisor/SupervisorAnnouncementsPage';
import BeltAssignmentsPage from './pages/Supervisor/BeltAssignmentsPage';
import BeltAssignmentDetailPage from './pages/Supervisor/BeltAssignmentDetailPage';
import BeltZoneQueuePage from './pages/Supervisor/BeltZoneQueuePage';
import WeatherAlertsPage from './pages/Supervisor/WeatherAlertsPage';

// Gate Display
import GateDisplayPage from './pages/GateDisplay/GateDisplayPage';

// Zone Display
import ZoneDisplayPage from './pages/ZoneDisplay/ZoneDisplayPage';

// Airport Announcement Pages
import AdminAirportAnnouncementsPage from './pages/Admin/AdminAirportAnnouncementsPage';
import AdminZonesPage from './pages/Admin/AdminZonesPage';
import AdminDestinationLanguagesPage from './pages/Admin/AdminDestinationLanguagesPage';
import SubmitAnnouncementPage from './pages/Operator/SubmitAnnouncementPage';
import OperatorAnnouncementsPage from './pages/Operator/OperatorAnnouncementsPage';

// Device Pages (Android tablet display)
import DeviceSetupPage from './pages/Device/DeviceSetupPage';
import DeviceDisplayPage from './pages/Device/DeviceDisplayPage';
import AdminDevicesPage from './pages/Admin/AdminDevicesPage';

// Other
import NotFoundPage from './pages/NotFoundPage';
import LoadingSpinner from './components/UI/LoadingSpinner';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/display" element={<DisplayPageSimple />} />
      <Route path="/login" element={user ? <UserRedirect /> : <LoginPage />} />
      <Route path="/register" element={user ? <UserRedirect /> : <RegisterPage />} />

      {/* ── Device Routes (no user auth — device token only) ──────────────── */}
      <Route path="/device-setup" element={<DeviceSetupPage />} />
      <Route path="/device-display" element={<DeviceDisplayPage />} />

      {/* ── Operator Routes (gate_operator, supervisor, manager, admin) ────────── */}
      <Route element={<ProtectedRoute><OperatorRoute /></ProtectedRoute>}>
        <Route element={<OperatorLayout />}>
          <Route path="/operator/session" element={<GateSessionPage />} />
          <Route path="/operator/adhoc" element={<AdHocPage />} />
          <Route path="/operator/request-template" element={<RequestTemplatePage />} />
          <Route path="/operator/help" element={<StaffHelpPage />} />
          <Route path="/operator/profile" element={<ProfilePage />} />
          <Route path="/operator/announce" element={<SubmitAnnouncementPage />} />
          <Route path="/operator/approvals" element={<OperatorApprovalsPage />} />
          <Route path="/announcements" element={<OperatorAnnouncementsPage />} />
          {/* Legacy route kept for any existing bookmarks */}
          <Route path="/operator/sequence/:id" element={<SessionQueueRedirect />} />
        </Route>
        {/* Full queue management — same powerful view as admin/supervisor */}
        <Route path="/operator/queue/:gateId" element={<AdminGateQueuePage />} />
      </Route>

      {/* ── Supervisor Routes (supervisor, admin) ─────────────────────────── */}
      <Route element={<ProtectedRoute><SupervisorRoute /></ProtectedRoute>}>
        {/* Pages inside the supervisor sidebar layout */}
        <Route element={<SupervisorLayout />}>
          <Route path="/supervisor/dashboard" element={<SupervisorDashboardPage />} />
          <Route path="/supervisor/announcements" element={<SupervisorAnnouncementsPage />} />
          <Route path="/supervisor/belts" element={<BeltAssignmentsPage />} />
          <Route path="/supervisor/belts/zone/:terminal/queue" element={<BeltZoneQueuePage />} />
          <Route path="/supervisor/belts/:id" element={<BeltAssignmentDetailPage />} />
          <Route path="/supervisor/weather-alerts" element={<WeatherAlertsPage />} />
          <Route path="/supervisor/profile" element={<ProfilePage />} />
          {/* CR 11 — Manager-only: requesting a new template. Reuses the supervisor
              layout/nav since Manager has the same base interface as Supervisor. */}
          <Route path="/manager/request-template" element={<RequestNewTemplatePage />} />
        </Route>
        {/* Full-screen queue management — full admin controls for supervisors */}
        <Route path="/supervisor/queue/:gateId" element={<AdminGateQueuePage />} />
        {/* Old session-based URL — forwards to the gate queue page */}
        <Route path="/supervisor/gate/:id" element={<SessionQueueRedirect />} />
      </Route>

      {/* ── Gate Display (any logged-in operator / supervisor / admin) ──────── */}
      <Route element={<ProtectedRoute><OperatorRoute /></ProtectedRoute>}>
        <Route path="/gate-display/:gateId" element={<GateDisplayPage />} />
        <Route path="/zone-display/:zoneCode" element={<ZoneDisplayPage />} />
      </Route>

      {/* ── Shared routes — all authenticated users ───────────────────────── */}
      {/* /announce and /my-announcements replace the old submission system     */}
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="/announce" element={<SubmitAnnouncementPage />} />
        <Route path="/my-announcements" element={<OperatorAnnouncementsPage />} />
        {/* Redirect old submission routes */}
        <Route path="/submissions/create" element={<Navigate to="/announce" replace />} />
        <Route path="/submissions" element={<Navigate to="/my-announcements" replace />} />
      </Route>

      {/* ── Admin + General Protected Routes ─────────────────────────────── */}
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<UserRedirect />} />

        {/* Admin-only */}
        <Route path="dashboard" element={<AdminRoute><DashboardPage /></AdminRoute>} />
        <Route path="templates" element={<AdminRoute><TemplatesPage /></AdminRoute>} />
        <Route path="profile" element={<AdminRoute><ProfilePage /></AdminRoute>} />

        {/* Admin namespace */}
        <Route path="admin" element={<AdminRoute />}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboardPage />} />
          <Route path="submissions" element={<Navigate to="/admin/airport-announcements" replace />} />
          <Route path="users" element={<AdminUsersPage />} />
          <Route path="templates" element={<AdminTemplatesPage />} />
          <Route path="template-requests" element={<AdminTemplateRequestsPage />} />
          <Route path="audit-log" element={<AdminAuditLogPage />} />
          <Route path="gates" element={<AdminGatesPage />} />
          <Route path="rules" element={<AdminRulesPage />} />
          <Route path="gate-sessions" element={<AdminGateSessionsPage />} />
          <Route path="gates/:gateId/queue" element={<AdminGateQueuePage />} />
          <Route path="airport-announcements" element={<AdminAirportAnnouncementsPage />} />
          <Route path="belt-assignments" element={<BeltAssignmentsPage />} />
          <Route path="belt-assignments/zone/:terminal/queue" element={<BeltZoneQueuePage />} />
          <Route path="belt-assignments/:id" element={<BeltAssignmentDetailPage />} />
          <Route path="weather-alerts" element={<WeatherAlertsPage />} />
          <Route path="zones" element={<AdminZonesPage />} />
          <Route path="destination-languages" element={<AdminDestinationLanguagesPage />} />
          <Route path="devices" element={<AdminDevicesPage />} />
          <Route path="flight-sync" element={<AdminFlightSyncPage />} />
          <Route path="reports" element={<AdminReportsPage />} />
        </Route>
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default App;
